import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import Text from "@/components/ui/text";
import MarkdownRenderer from "@/components/ui/markdown-renderer";
import { useAttachmentDownloader } from "@/entities/attachments/hooks";
import { EntityType } from "@/services/hooks/entities/useEntityType";
import { cn } from "@/utils/utils";
import { Button } from "@/components/ui/button";
import { useDealSummary } from "@/pages/deals/hooks";
import { useIsDemoDeal, useIsDemoLead } from "@/assets/mockupData";

const dealSummary =
  "The deal is currently in the POC stage with an estimated close date of December 31st, 2025. The Decision Maker recently changed to Don Muse, IT director. Following a positive demo, the next step is signing an NDA, meeting to discuss the POC scope, and addressing any remaining questions, particularly regarding price, as competitor Globex Corp has presented a lower offer. While the overall sentiment is positive, securing the deal may involve navigating price sensitivity.";
const leadSummary =
  "Alnatura, a chain of organic food supermarkets and producer of organic food with an estimated 3,500 employees, emerged as a warm lead from an industry webinar on cloud computing, with Head of the Supply Chain Service Aaron Beyer expressing strong interest in our services due to challenges with their legacy systems. This indicates a potential need for cloud migration and infrastructure modernization, making the next crucial step scheduling a discovery call to delve into their specific requirements and timeline.";

interface Props {
  tPrefix: string;
  entityType: EntityType.DEAL | EntityType.LEAD;
  entityId: string;
  onHasContent?: (hasContent: boolean) => void;
}

const Summary: React.FC<Props> = ({
  tPrefix,
  entityType,
  entityId,
  onHasContent
}) => {
  const { t } = useTranslation();
  const downloadAttachment = useAttachmentDownloader();
  const [isSummaryExpanded, setIsSummaryExpanded] = useState(false);
  const isDemoDeal = useIsDemoDeal(entityId);
  const isDemoLead = useIsDemoLead(entityId);
  const isDemo = entityType === EntityType.DEAL ? isDemoDeal : isDemoLead;

  const { data: summaryData, isLoading } = useDealSummary(entityId, {
    enabled: entityType === EntityType.DEAL && !isDemo
  });

  const summary = isDemo
    ? entityType === EntityType.DEAL
      ? dealSummary
      : leadSummary
    : summaryData?.overviewParagraph || "";

  React.useEffect(() => {
    if (isDemo || !isLoading) {
      onHasContent?.(!!summary);
    }
  }, [isDemo, isLoading, summary, onHasContent]);

  if ((!isDemo && isLoading) || !summary) {
    return null;
  }

  return (
    <div className="flex flex-col gap-2 p-2">
      <Text variant="bodySecondary" className="text-h2">
        {t(`${tPrefix}.summary`)}
      </Text>
      <div className="flex flex-col gap-1">
        <MarkdownRenderer
          content={summary}
          className={cn(
            "max-w-full overflow-hidden text-ellipsis transition-all duration-300 ease-in-out",
            isSummaryExpanded ? "max-h-96" : "max-h-20 line-clamp-3"
          )}
          onDownloadAttachment={downloadAttachment}
        />
        <Button
          variant="tertiary"
          className="w-fit h-fit self-end"
          onClick={() => setIsSummaryExpanded(!isSummaryExpanded)}
        >
          {t(isSummaryExpanded ? "readLess" : "readMore")}
        </Button>
      </div>
    </div>
  );
};

export default Summary;
